// ---------------------------------------------------------------------------
// OTP verify flow with approval-resume recovery (AUTH-177324).
//
// On the Android shell the verify RPC occasionally resolves with an empty or
// undefined body: the server completed the Twilio check and recorded the
// approval, but the response body never reached the client. Retrying the same
// call cannot help when the transport is the thing dropping the body, so after
// the retries we ask the server to resume from the recorded approval — a small
// lookup-only call that returns a fresh sign-in token.
//
// Pure and dependency-injected so it can be tested without Twilio or network.
// ---------------------------------------------------------------------------

export type VerifySuccess = {
  ok: true;
  email: string;
  token_hash: string;
  user_id: string;
};

export type VerifyFailure = { ok: false; code?: string; message?: string };

export type VerifyOutcome =
  | { outcome: "ok"; result: VerifySuccess; resumed: boolean }
  | { outcome: "failed"; result: VerifyFailure }
  | { outcome: "malformed"; stage: string; shape?: string }
  | { outcome: "call_error"; error: unknown };

/**
 * Tolerates string bodies and arbitrarily nested RPC envelopes.
 *
 * AUTH-811656: on the Android shell the verify RPC returned HTTP 200 with a
 * non-empty JSON body, yet the client still reported "empty verify response".
 * The body was a nested transport envelope (e.g. `{ result: { json: {...} } }`)
 * that the previous single-level unwrap could not see through, so a perfectly
 * good verification was thrown away. Search the payload depth-first instead of
 * assuming one known wrapper key.
 */
export function unwrapVerifyBody(raw: unknown, depth = 0): unknown {
  if (depth > 6) return null;

  let res: any = raw;
  if (typeof res === "string") {
    try {
      res = JSON.parse(res);
    } catch {
      return null;
    }
  }
  if (!res || typeof res !== "object") return null;

  // Direct hit: a typed verify result.
  if ("ok" in res) return res;
  // A success payload that lost its `ok` flag is still usable.
  if (typeof res.token_hash === "string" && res.token_hash.length > 0) {
    return { ...res, ok: true };
  }

  const candidates: unknown[] = Array.isArray(res)
    ? res
    : [res.result, res.data, res.json, res.body, res.payload, res.value].filter((v) => v != null);

  for (const candidate of candidates) {
    const hit = unwrapVerifyBody(candidate, depth + 1);
    if (hit) return hit;
  }
  return null;
}

/** Safe, PII-free description of an unrecognised body, for the error report. */
export function describeBodyShape(raw: unknown): string {
  if (raw === undefined) return "undefined";
  if (raw === null) return "null";
  if (typeof raw === "string") return `string(${raw.length})`;
  if (Array.isArray(raw)) return `array(${raw.length})`;
  if (typeof raw === "object") return `object{${Object.keys(raw as object).slice(0, 8).join(",")}}`;
  return typeof raw;
}


export async function verifyWithResume(deps: {
  verify: () => Promise<unknown>;
  resume: () => Promise<unknown>;
  attempts?: number;
}): Promise<VerifyOutcome> {
  const attempts = Math.max(1, deps.attempts ?? 2);
  let lastRaw: unknown;

  for (let i = 0; i < attempts; i++) {
    let raw: unknown;
    try {
      raw = await deps.verify();
    } catch (error) {
      return { outcome: "call_error", error };
    }
    lastRaw = raw;
    const body = unwrapVerifyBody(raw) as VerifySuccess | VerifyFailure | null;
    if (body) {
      // A typed failure is authoritative — never retry or resume past it.
      return body.ok === true
        ? { outcome: "ok", result: body, resumed: false }
        : { outcome: "failed", result: body };
    }
  }

  // Every body was unreadable. The OTP was very likely already approved
  // server-side; resume from the approval ledger instead of dead-ending.
  let rawResume: unknown;
  try {
    rawResume = await deps.resume();
  } catch {
    return { outcome: "malformed", stage: "twilio_verify", shape: describeBodyShape(lastRaw) };
  }
  const resumed = unwrapVerifyBody(rawResume) as VerifySuccess | VerifyFailure | null;
  if (resumed?.ok === true) return { outcome: "ok", result: resumed, resumed: true };
  if (resumed) return { outcome: "failed", result: resumed as VerifyFailure };

  return {
    outcome: "malformed",
    stage: "twilio_verify",
    shape: `verify=${describeBodyShape(lastRaw)} resume=${describeBodyShape(rawResume)}`,
  };
}
